import { Colors } from '@zep-us/zus/tokens';
import { TextLongLeftIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { CheckSmallCircleFillIcon, MessageLinesFillIcon, SquareGridSemiFillIcon } from '@/icons';
import { LocalizedOxTypeIcon } from '../LocalizedOxTypeIcon';
import { TypeBadge } from './TypeBadge';
import { type QuizType } from './types';

/**
 * QuizTypeBadge - 퀴즈 타입 배지 (객관식/주관식/OX)
 * 일관된 스타일링을 위해 TypeBadge 컴포넌트 사용
 * EditableQuizCard의 QuizTypeSelectorButton과 일치 (읽기 전용, 드롭다운 없음)
 * @component
 */

interface QuizTypeBadgeProps {
  type: QuizType;
  className?: string;
}

/**
 * EditableQuizCard/QuizTypeSelector/QuizTypeIcons.tsx와 일치하는 퀴즈 타입 아이콘 맵
 */
const QUIZ_TYPE_ICON_MAP: Record<QuizType, React.JSX.Element | undefined> = {
  multiple: <CheckSmallCircleFillIcon size="xs" fill={Colors.Green600} />,
  subjective: <MessageLinesFillIcon size="xs" fill={Colors.Blue500} />,
  ox: <LocalizedOxTypeIcon size="xs" fill={Colors.Purple400} />,
  essay: <TextLongLeftIcon size="xs" fill={Colors.Pink400} />,
  match: <SquareGridSemiFillIcon size="xs" fill={Colors.Yellow700} />
};

/**
 * QuizTypeBadge 컴포넌트 - 퀴즈 특정 설정이 있는 TypeBadge 래퍼
 * TypeBadge 컴포넌트를 사용하여 아이콘과 함께 퀴즈 타입 표시 (읽기 전용, 드롭다운 없음)
 */
export const QuizTypeBadge = ({
  type,
  className
}: QuizTypeBadgeProps) => {
  const {
    t
  } = useTranslation(['common']);
  const TYPE_LABELS: Record<QuizType, string> = {
    multiple: t('quizCard.typeBadge.multiple'),
    subjective: t('quizCard.typeBadge.subjective'),
    ox: t('quizCard.typeBadge.ox'),
    essay: t('quizCard.typeBadge.essay'),
    match: t('quizCard.typeBadge.match')
  };
  return <TypeBadge icon={QUIZ_TYPE_ICON_MAP[type]} label={TYPE_LABELS[type]} className={className} data-sentry-element="TypeBadge" data-sentry-component="QuizTypeBadge" data-sentry-source-file="QuizTypeBadge.tsx" />;
};